import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { isTreeLogActive, useLogObserver } from "../../hooks/use-logObserver";
import { Well, Wellbore, Log } from "../../store/types";
import { useNavigateApp } from "../../hooks/use-navigateApp";
import { useWellsAPI } from "../../hooks/api/use-wellsAPI";
import { useWellboresAPI } from "../../hooks/api/use-wellboresAPI";
import { useLogsAPI } from "../../hooks/api/use-logsAPI";
import { useCurrentUser } from "../../auth/use-currentUser";
import { Permissions } from "../../auth/permissions";
import { Menu, MenuItem } from "react-pro-sidebar";
import TreeView from "../TreeView/TreeView";
import Logo from "../Logo/Logo";
import {
  StyledSidebar,
  TreeContainer,
  TreeViewContainer,
  LogoContainer,
  CloseButton,
} from "./SideMenu.styles";

const SideMenu: React.FC = () => {
  const { activePage, isClosed, navigateTo, setMenuClosed } = useNavigateApp();
  const [searchParams] = useSearchParams();
  const { isObserved, toggleObserve } = useLogObserver();
  const { can } = useCurrentUser();
  const canDeleteLogs = can(Permissions.LogsDelete);

  const { wells, isFetchLoading: isWellsLoading } = useWellsAPI({
    fetchAll: true,
  });
  const {
    wellbores,
    fetchWellbores,
    isLoading: isWellboresLoading,
  } = useWellboresAPI({ fetchAll: false });
  const {
    logs,
    fetchLogs,
    deleteLog,
    isFetchLoading: isLogsLoading,
  } = useLogsAPI({ fetchAll: false });
  const [selectedWellbore, setSelectedWellbore] = useState<Wellbore | null>(
    null
  );

  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [stableWells, setStableWells] = useState<Well[]>([]);
  const prevWellsStr = useRef("");
  const [stableWellbores, setStableWellbores] = useState<
    Wellbore[] | undefined
  >();
  const prevWellboresStr = useRef("");
  const [stableLogs, setStableLogs] = useState<Log[] | undefined>();
  const prevLogsStr = useRef("");
  const prevSelectedRef = useRef<string | null>(null);

  useEffect(() => {
    if (activePage.startsWith("wellbore/view")) {
      const wellboreId = searchParams.get("id");
      const wellUid = searchParams.get("wellUid");
      if (wellboreId && wellUid) {
        const key = `${wellboreId}|${wellUid}`;
        if (prevSelectedRef.current !== key) {
          prevSelectedRef.current = key;
          setSelectedWellbore({
            uid: wellboreId,
            wellUid,
            name: "",
          } as Wellbore);
        }
      } else {
        if (prevSelectedRef.current !== null) {
          prevSelectedRef.current = null;
          setSelectedWellbore(null);
        }
      }
    } else {
      if (prevSelectedRef.current !== null) {
        prevSelectedRef.current = null;
        setSelectedWellbore(null);
      }
    }
  }, [activePage, searchParams]);

  useEffect(() => {
    if (isWellsLoading || isWellboresLoading || isLogsLoading)
      setIsDataLoading(true);
    else setIsDataLoading(false);
  }, [isWellsLoading, isWellboresLoading, isLogsLoading]);

  useEffect(() => {
    const str = JSON.stringify(wells);
    if (str !== prevWellsStr.current) {
      prevWellsStr.current = str;
      setStableWells(wells || []);
    }
  }, [wells]);

  useEffect(() => {
    const str = JSON.stringify(wellbores);
    if (str !== prevWellboresStr.current) {
      prevWellboresStr.current = str;
      setStableWellbores(wellbores);
    }
  }, [wellbores]);

  useEffect(() => {
    const str = JSON.stringify(logs);
    if (str !== prevLogsStr.current) {
      prevLogsStr.current = str;
      setStableLogs(logs);
    }
  }, [logs]);

  const handleToggleMenu = () => {
    if (isClosed) {
      setMenuClosed(false);
    } else setMenuClosed(true);
  };

  const handleViewWellbore = useCallback(
    (wellbore: Wellbore) => {
      setSelectedWellbore(wellbore);
      navigateTo(
        `wellbore/view?id=${wellbore.uid}&wellUid=${wellbore.wellUid}`
      );
    },
    [navigateTo]
  );

  const handleDeleteLog = useCallback(
    (log: Log) => deleteLog(log.wellUid, log.wellboreUid, log.uid),
    [deleteLog]
  );

  return (
    <StyledSidebar
      collapsed={isClosed}
      collapsedWidth="52px"
      width="300px"
      className="tabview-custom"
    >
      <Menu className="tabview-custom">
        <MenuItem
          className="default-menu-item custom-tree-menu-item"
          onClick={handleToggleMenu}
        >
          <TreeContainer>
            <LogoContainer>
              <Logo onClick={handleToggleMenu} />
              <CloseButton>
                <i
                  className="pi pi-chevron-left"
                  style={{ fontSize: "0.85rem" }}
                  onClick={handleToggleMenu}
                />
              </CloseButton>
            </LogoContainer>

            <TreeViewContainer
              onClick={(e) => e.stopPropagation()}
              $isClosed={isClosed}
            >
              <TreeView
                wells={stableWells}
                wellbores={stableWellbores}
                logs={stableLogs}
                isDataLoading={isDataLoading}
                fetchWellbores={fetchWellbores}
                fetchLogs={fetchLogs}
                onViewWellbore={handleViewWellbore}
                selectedWellbore={selectedWellbore}
                isObserved={isObserved}
                isLogActive={isTreeLogActive}
                onToggleObserve={toggleObserve}
                onDeleteLog={canDeleteLogs ? handleDeleteLog : undefined}
                treeControls={false}
                isContextMenu={false}
              />
            </TreeViewContainer>
          </TreeContainer>
        </MenuItem>
      </Menu>
    </StyledSidebar>
  );
};

export default React.memo(SideMenu);
